import React, { useEffect, useState, useRef, useContext } from "react";
import SignatureField from "../SignatureField";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import "../../assets/css/signature.css";
import { getPreviewUrlFromPath } from "../../utils/fileHelpers";
import secureAxios from "../../utils/secureAxios";

const EditMonthlyReport = ({ report, onCancel, onUpdated }) => {
  const { tenant_id } = useContext(AuthContext).user;
  const apiBase = getApiBaseURL();

  const { user } = useContext(AuthContext);
  const userId = user?.user_id;

  const [formData, setFormData] = useState({});
  const [authorizations, setAuthorizations] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Signature values
  const [consumerSignature, setConsumerSignature] = useState("");
  const [staffSignature, setStaffSignature] = useState("");
  const [otherSignature, setOtherSignature] = useState("");

  // Signature canvas refs
  const consumerCanvasRef = useRef();
  const staffCanvasRef = useRef();
  const otherCanvasRef = useRef();

  const reportNumber = formData.reportNumber;

  // Fetch available statuses
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        // Get all statuses and filter by status_type_id = 1
        const response = await secureAxios.get(`${apiBase}/api/status`);
        const monthlyReportStatuses = response.data.filter(
          (status) => status.status_type_id === 1
        );
        setStatuses(monthlyReportStatuses);
      } catch (err) {
        alert("Unable to load status options. Please try again.");
      }
    };

    if (tenant_id) {
      fetchStatuses();
    }
  }, [tenant_id, apiBase]);

  useEffect(() => {
    if (report?.report_id) {
      secureAxios
        .get(`${apiBase}/api/reports/${report.report_id}`, {
          params: { tenant_id },
        })
        .then((res) => {
          const data = res.data;

          setFormData({
            ...data,
            report_id: report.report_id,
            date_of_report: data.date_of_report?.split("T")[0] ?? null,
            estimated_completion_date:
              data.estimated_completion_date?.split("T")[0] ?? null,
            consumer_signature_date:
              data.consumer_signature_date?.split("T")[0] ?? null,
            staff_signature_date:
              data.staff_signature_date?.split("T")[0] ?? null,
            other_signature_date:
              data.other_signature_date?.split("T")[0] ?? null,
          });
          const consumerSig = data.consumer_signature_base64 || "";
          const staffSig = data.staff_signature_base64 || "";
          const otherSig = data.other_signature_base64 || "";

          setConsumerSignature(consumerSig);
          setStaffSignature(staffSig);
          setOtherSignature(otherSig);
        })
        .catch((err) => alert("Failed to load report. Please try again."))
        .finally(() => setLoading(false));
    }
  }, [apiBase, report?.report_id, tenant_id]);

  useEffect(() => {
    if (formData.consumer_id && tenant_id) {
      const loadAuthorizations = async () => {
        try {
          const response = await secureAxios.get(
            `${apiBase}/api/auths/consumer/${formData.consumer_id}`,
            {
              params: {
                tenant_id: tenant_id,
                auth_type: "service", // ✅ Only load service authorizations
              },
            }
          );
          setAuthorizations(response.data || []);
        } catch (err) {
          console.error("Failed to load authorizations:", err);
          setAuthorizations([]);
        }
      };

      loadAuthorizations();
    }
  }, [apiBase, formData.consumer_id, tenant_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearSignature = (ref, setValue) => {
    if (ref?.current?.clear) {
      ref.current.clear();
      setValue("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const reportId = formData?.report_id;

    if (!reportId) {
      alert("Cannot update report: Missing report ID.");
      return;
    }

    // Get signature data - always try toDataURL first, then fall back to state
    const getSignatureData = (canvasRef, stateValue) => {
      if (canvasRef.current?.toDataURL) {
        const canvasData = canvasRef.current.toDataURL();
        return canvasData || stateValue || "";
      }
      return stateValue || "";
    };

    const payload = {
      ...formData,
      tenant_id,
      consumer_id: formData.consumer_id,
      user_id: userId,
      service_id: formData.service_id,
      authorization_id: formData.authorization_id,
      report_name: formData.report_name,
      report_number: reportNumber,
      date_of_report: formData.date_of_report,
      estimated_completion_date: formData.estimated_completion_date,
      goal: formData.goal,
      objectives: formData.objectives,
      results: formData.results,
      productivity: formData.productivity,
      assessment: formData.assessment,
      consumer_assessment: formData.consumer_assessment,
      post_test: formData.post_test,
      recommendations: formData.recommendations,
      consumer_signature_base64: getSignatureData(
        consumerCanvasRef,
        consumerSignature
      ),
      consumer_signature_date: formData.consumer_signature_date,
      staff_signature_base64: getSignatureData(staffCanvasRef, staffSignature),
      staff_signature_date: formData.staff_signature_date,
      other_signature_base64: getSignatureData(otherCanvasRef, otherSignature),
      other_signature_date: formData.other_signature_date,
      status_id: formData.status_id,
    };

    try {
      const response = await secureAxios.put(
        `${apiBase}/api/reports/${reportId}`,
        payload
      );

      alert("Report updated successfully! A secure PDF has been generated.");

      const previewUrl = getPreviewUrlFromPath(
        response.data.documentPath,
        apiBase
      );

      // Log audit for viewing the updated PDF
      try {
        await secureAxios.post(`${apiBase}/api/audit-logs/log`, {
          action: "VIEW_MONTHLY_REPORT_PDF",
          entity_type: "MONTHLY_REPORT",
          entity_id: reportId,
          entity_identifier: `Updated PDF for Report ${reportNumber}`,
          purpose_of_use: "TREATMENT",
          contains_phi: true,
          phi_types: ["medical_information", "service_records"],
          data_sensitivity_level: "high",
          fields_accessed: ["full_report_pdf"],
        });
      } catch (auditErr) {
        // Silent fail for audit logging
      }

      window.open(previewUrl, "_blank");

      if (onUpdated) onUpdated();
    } catch (err) {
      alert("Failed to update report. Please try again.");
    }
  };

  if (loading) return <p>Loading report...</p>;

  return (
    <form onSubmit={handleSubmit}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Edit Monthly Report</h3>
        <span className="badge bg-warning text-dark">
          <i className="bi bi-shield-lock me-1"></i>
          HIPAA Protected Data
        </span>
      </div>

      <div className="alert alert-info mb-3">
        <i className="bi bi-info-circle me-2"></i>
        <strong>Privacy Notice:</strong> This form contains Protected Health
        Information (PHI). Access is logged and monitored for HIPAA compliance.
        Only authorized personnel should view or edit this information.
      </div>

      <div className="mb-3">
        <label>Consumer Name</label>
        <input
          type="text"
          className="form-control"
          value={formData.consumer_name || "—"}
          disabled
          readOnly
          style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
        />
        <small className="text-muted">
          <i className="bi bi-lock-fill me-1"></i>
          Consumer cannot be changed after report creation
        </small>
      </div>

      <div className="mb-3">
        <label>Authorization</label>
        <select
          className="form-select"
          name="authorization_id"
          value={formData.authorization_id || ""}
          onChange={handleChange}
          required
        >
          <option value="">Select Authorization</option>
          {authorizations.map((auth) => (
            <option key={auth.authorization_id} value={auth.authorization_id}>
              {auth.authorization_number} – {auth.service_name}
            </option>
          ))}
        </select>
      </div>

      {[
        "date_of_report",
        "estimated_completion_date",
        "goal",
        "objectives",
        "results",
        "productivity",
        "assessment",
        "consumer_assessment",
        "post_test",
        "recommendations",
      ].map((field) => (
        <div key={field} className="mb-3">
          <label>
            {field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
          </label>
          {field.includes("date") ? (
            <input
              type="date"
              className="form-control"
              name={field}
              value={formData[field] || ""}
              onChange={handleChange}
            />
          ) : (
            <textarea
              className="form-control"
              name={field}
              value={formData[field] || ""}
              onChange={handleChange}
              placeholder="Enter confidential information here..."
            />
          )}
        </div>
      ))}

      <div className="alert alert-warning mb-3">
        <i className="bi bi-exclamation-triangle me-2"></i>
        <strong>Signature Requirements:</strong> Electronic signatures are
        legally binding and must be completed by the authorized individual only.
      </div>

      {[
        {
          label: "Consumer Signature",
          value: consumerSignature,
          setValue: setConsumerSignature,
          ref: consumerCanvasRef,
          dateField: "consumer_signature_date",
        },
        {
          label: "Staff Signature",
          value: staffSignature,
          setValue: setStaffSignature,
          ref: staffCanvasRef,
          dateField: "staff_signature_date",
        },
        {
          label: "Other Signature",
          value: otherSignature,
          setValue: setOtherSignature,
          ref: otherCanvasRef,
          dateField: "other_signature_date",
        },
      ].map(({ label, value, setValue, ref, dateField }) => (
        <div className="col-md-12 mb-4" key={label}>
          <label className="form-label">{label}</label>
          <div style={{ border: "1px solid #ccc" }}>
            <SignatureField
              value={value}
              onChange={setValue}
              penColor="blue"
              ref={ref}
            />
          </div>
          <div className="row align-items-center mt-2">
            <div className="col-md-6">
              <label className="form-label">{label} Date</label>
              <input
                type="date"
                className="form-control"
                name={dateField}
                value={formData[dateField] || ""}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-6 mt-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => handleClearSignature(ref, setValue)}
              >
                Clear Signature
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Status Selector - Moved to bottom */}
      <div className="mb-3">
        <label>Report Status</label>
        <select
          className="form-select"
          name="status_id"
          value={formData.status_id || ""}
          onChange={handleChange}
        >
          <option value="">Select Status</option>
          {Array.isArray(statuses) &&
            statuses.map((status) => (
              <option key={status.status_id} value={status.status_id}>
                {status.status}
              </option>
            ))}
        </select>
      </div>

      <div className="d-flex justify-content-between">
        <button type="submit" className="btn btn-success">
          <i className="bi bi-save me-2"></i>
          Save Changes Securely
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
};

export default EditMonthlyReport;
